(function () {
  'use strict';

  const SERVICE_HANDLES = ['logo-customisation', 'industry-standard-logos', 'logo-setup-digitisation'];
  const LOGO_PROPERTY_NAMES = [
    'Customisation Type', 'Logo Position', 'Industry Standard Logo', 'Logo Variant', 'Logo Price',
    'Logo Customisation', 'Additional Logo Number', 'Uploaded Filename', 'Uploaded Logo',
    'Uploaded Logo File', 'Uploaded Logo URL', 'Logo Notes', 'Logo Digitisation',
    'Digitisation Source', 'Applied To', '_TPW Customisation ID',
  ];
  const busyBundles = new Set();
  let bundleCounter = 0;
  let confirmationResolver = null;
  let confirmationPreviousFocus = null;
  let confirmationDrawer = null;

  function routesRoot() {
    return window.Shopify && window.Shopify.routes && window.Shopify.routes.root
      ? window.Shopify.routes.root
      : '/';
  }

  function endpoint(path) {
    return routesRoot() + String(path || '').replace(/^\//, '');
  }

  function make(tag, className, textValue) {
    const element = document.createElement(tag);
    if (className) element.className = className;
    if (typeof textValue !== 'undefined') element.textContent = textValue;
    return element;
  }

  function intValue(value, fallback) {
    const parsed = parseInt(value, 10);
    return Number.isFinite(parsed) ? parsed : (fallback || 0);
  }

  function lineNumber(line, name) {
    return line ? Math.max(0, intValue(line.dataset[name], 0)) : 0;
  }

  function cloneProperties(properties) {
    const output = {};
    Object.keys(properties || {}).forEach((key) => {
      if (properties[key] !== null && typeof properties[key] !== 'undefined') output[key] = properties[key];
    });
    return output;
  }

  function formatMoney(cents, drawer) {
    const amount = Math.max(0, Number(cents) || 0);
    const moneyFormat = drawer ? drawer.dataset.moneyFormat || '' : '';
    const currency = drawer ? drawer.dataset.currency || 'GBP' : 'GBP';
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(amount, moneyFormat);
    }
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {
        style: 'currency',
        currency,
      }).format(amount / 100);
    } catch (error) {
      return '£' + (amount / 100).toFixed(2);
    }
  }

  async function requestJson(path, options) {
    const response = await fetch(endpoint(path), Object.assign({
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    }, options || {}));
    const body = await response.json().catch(() => null);
    if (!response.ok) {
      const message = body && (body.description || body.message)
        ? body.description || body.message
        : 'Shopify returned status ' + response.status + '.';
      throw new Error(message);
    }
    return body;
  }

  function fetchCart() {
    return requestJson('cart.js');
  }

  function changeLine(keyOrLine, quantity, properties) {
    const body = { id: keyOrLine, quantity: Math.max(0, intValue(quantity, 0)) };
    if (typeof properties !== 'undefined') body.properties = cloneProperties(properties);
    return requestJson('cart/change.js', {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
  }

  function updateCartQuantities(updates) {
    return requestJson('cart/update.js', {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates }),
    });
  }

  function itemHandle(item) {
    return String(item && (item.handle || item.product_handle) || '').toLowerCase();
  }

  function isSeparateCustomisationItem(item) {
    return SERVICE_HANDLES.indexOf(itemHandle(item)) !== -1;
  }

  function getBundleId(item) {
    return item && item.properties ? String(item.properties['Custom Bundle ID'] || '') : '';
  }

  function findGroupItems(cart, bundleId) {
    return (cart.items || []).filter((item) => getBundleId(item) === String(bundleId || ''));
  }

  function parseBundleContents(value) {
    return String(value || '')
      .replace(/<br\s*\/?>/gi, '\n')
      .split(/\n+/)
      .map((line) => {
        const clean = line.replace(/^\s*[•\-]\s*/, '').trim();
        if (!clean) return null;
        const match = clean.match(/^(.*?)(?:\s+[x×]\s*)(\d+)\s*$/i);
        return match
          ? { base: match[1].trim(), quantity: Math.max(0, intValue(match[2], 0)) }
          : { base: clean, quantity: 1 };
      })
      .filter(Boolean);
  }

  function cleanVariantTitle(value) {
    const title = String(value || '').trim();
    return title === 'Default Title' ? '' : title;
  }

  function makeAppliedLabel(value) {
    return String(value || '').replace(/^([^:]+):\s*/, '$1 - ').replace(/\s+/g, ' ').trim();
  }

  function getGroupTargets(items) {
    const garments = (items || []).filter((item) => !isSeparateCustomisationItem(item) && item.quantity > 0);
    const bundleBase = garments.find((item) => item.properties && item.properties['Bundle Contents']);
    if (bundleBase) {
      const entries = parseBundleContents(bundleBase.properties['Bundle Contents']);
      const contentsTotal = entries.reduce((sum, entry) => sum + entry.quantity, 0);
      const single = String(bundleBase.properties['_Bundle Quantity Applied'] || '') === 'true';
      const flexible = !single && contentsTotal > 0 && contentsTotal === bundleBase.quantity;
      const multiplier = flexible ? 1 : Math.max(1, bundleBase.quantity || 1);
      return entries.map((entry, index) => ({
        key: 'virtual:' + bundleBase.key + ':' + index,
        baseKey: bundleBase.key,
        index,
        actual: false,
        item: bundleBase,
        label: entry.base,
        appliedLabel: makeAppliedLabel(entry.base),
        quantity: entry.quantity * multiplier,
        perBundleQuantity: entry.quantity,
        mode: single ? 'single' : (flexible ? 'flexible' : 'fixed'),
      }));
    }
    return garments.map((item) => {
      const variant = cleanVariantTitle(item.variant_title);
      let label = String(item.product_title || item.title || 'Garment');
      if (variant) label += ' – ' + variant;
      return {
        key: 'line:' + item.key,
        lineKey: item.key,
        actual: true,
        item,
        label,
        appliedLabel: label,
        quantity: item.quantity,
        mode: 'line',
      };
    });
  }

  function splitAppliedTo(value) {
    const text = String(value || '').replace(/\r/g, '').trim();
    if (!text) return [];
    let rows = text.split(/\n+/).filter(Boolean);
    if (rows.length === 1 && rows[0].indexOf(',') !== -1) rows = rows[0].split(/,\s*/);
    return rows.map((row) => row.trim()).filter(Boolean);
  }

  function normaliseAppliedBase(value) {
    return String(value || '')
      .replace(/\s+[x×]\s*\d+\s*$/i, '')
      .replace(/[–—]/g, '-')
      .replace(/\s*:\s*/g, ' - ')
      .replace(/\s*\/\s*/g, ' / ')
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();
  }

  function targetMatchesAppliedEntry(target, entry) {
    const targetBase = normaliseAppliedBase(target.appliedLabel || target.label);
    const entryBase = normaliseAppliedBase(entry);
    return !!targetBase && !!entryBase && (
      targetBase === entryBase || targetBase.indexOf(entryBase) !== -1 || entryBase.indexOf(targetBase) !== -1
    );
  }

  function targetsForCustomisationItem(item, targets) {
    const entries = splitAppliedTo(item.properties && item.properties['Applied To']);
    if (!entries.length) return targets.slice();
    const matches = targets.filter((target) => entries.some((entry) => targetMatchesAppliedEntry(target, entry)));
    return matches.length ? matches : targets.slice();
  }

  function formatAppliedTo(targets) {
    return (targets || []).map((target) => target.appliedLabel + ' × ' + target.quantity).join('\n');
  }

  function lineLogoApplies(garmentLine, logoLine) {
    const applied = logoLine.dataset.tpwAppliedTo || '';
    if (!applied) return true;
    const product = garmentLine.dataset.tpwProductTitle || '';
    const variant = cleanVariantTitle(garmentLine.dataset.tpwVariantTitle || '');
    const full = product + (variant ? ' – ' + variant : '');
    return splitAppliedTo(applied).some((entry) => {
      const normalEntry = normaliseAppliedBase(entry);
      const normalFull = normaliseAppliedBase(full);
      const normalVariant = normaliseAppliedBase(variant);
      return normalFull === normalEntry || normalFull.indexOf(normalEntry) !== -1 ||
        normalEntry.indexOf(normalFull) !== -1 || (normalVariant && normalEntry.indexOf(normalVariant) !== -1);
    });
  }

  function inferredLogoType(line) {
    const explicit = line.dataset.tpwCustomisationType || '';
    if (explicit) return explicit;
    const position = String(line.dataset.tpwLogoPosition || '').toLowerCase();
    if (position.indexOf('embroider') !== -1) return 'Embroidery';
    if (position.indexOf('print') !== -1) return 'Print';
    return line.dataset.tpwProductTitle || 'Logo customisation';
  }

  function createLogoRow(logoLine, garmentLine, drawer) {
    const row = make('div', 'tpw-drawer-logo-row');
    const media = make('div', 'tpw-drawer-logo-row__image');
    const info = make('div', 'tpw-drawer-logo-row__info');
    const price = make('div', 'tpw-drawer-logo-row__price');
    const included = logoLine === garmentLine && logoLine.dataset.tpwLogoCustomisation === 'Included';
    const imageUrl = logoLine.dataset.tpwLogoUrl || (included ? '' : logoLine.dataset.tpwImageUrl) || '';
    const quantity = lineNumber(garmentLine, 'tpwQuantity');
    const allocatedCost = lineNumber(logoLine, 'tpwUnitPrice') * quantity;
    const position = logoLine.dataset.tpwLogoPosition || '';
    const detail = logoLine.dataset.tpwIndustryLogo || logoLine.dataset.tpwLogoFile || logoLine.dataset.tpwLogoNotes || '';
    if (imageUrl) {
      const image = document.createElement('img');
      image.src = imageUrl;
      image.alt = inferredLogoType(logoLine) + ' preview';
      image.loading = 'lazy';
      media.appendChild(image);
    }
    info.appendChild(make('strong', '', inferredLogoType(logoLine)));
    if (position) info.appendChild(make('span', '', 'Position: ' + position.split('/').pop().trim()));
    if (detail) info.appendChild(make('small', '', detail));
    price.textContent = included ? 'Included' : formatMoney(allocatedCost, drawer);
    row.appendChild(media);
    row.appendChild(info);
    row.appendChild(price);
    return row;
  }

  function createProductCard(line, logoLines, drawer) {
    const card = make('article', 'tpw-drawer-product');
    const media = make('a', 'tpw-drawer-product__image');
    const info = make('div', 'tpw-drawer-product__info');
    const price = make('div', 'tpw-drawer-product__price', formatMoney(lineNumber(line, 'tpwLinePrice'), drawer));
    const title = make('a', 'tpw-drawer-product__title', line.dataset.tpwProductTitle || 'Garment');
    const variant = cleanVariantTitle(line.dataset.tpwVariantTitle || '');
    const relevantLogos = logoLines.filter((logoLine) => lineLogoApplies(line, logoLine));
    media.href = line.dataset.tpwProductUrl || '#';
    title.href = line.dataset.tpwProductUrl || '#';
    if (line.dataset.tpwImageUrl) {
      const image = document.createElement('img');
      image.src = line.dataset.tpwImageUrl;
      image.alt = line.dataset.tpwProductTitle || 'Garment';
      image.loading = 'lazy';
      media.appendChild(image);
    }
    info.appendChild(title);
    if (variant) info.appendChild(make('span', 'tpw-drawer-product__variant', variant));
    info.appendChild(make('span', 'tpw-drawer-product__qty', 'Quantity ' + lineNumber(line, 'tpwQuantity')));
    card.appendChild(media);
    card.appendChild(info);
    card.appendChild(price);
    if (relevantLogos.length || line.dataset.tpwLogoCustomisation === 'Included') {
      const logos = make('div', 'tpw-drawer-product__logos');
      logos.appendChild(make('p', 'tpw-drawer-product__logos-title', 'Logos applied to this item'));
      if (line.dataset.tpwLogoCustomisation === 'Included') logos.appendChild(createLogoRow(line, line, drawer));
      relevantLogos.forEach((logoLine) => logos.appendChild(createLogoRow(logoLine, line, drawer)));
      card.appendChild(logos);
    }
    return card;
  }

  function createDigitisationCard(line, drawer) {
    const card = make('div', 'tpw-drawer-digitisation');
    const info = make('div', 'tpw-drawer-digitisation__info');
    info.appendChild(make('strong', '', 'Logo digitisation'));
    info.appendChild(make('span', '', line.dataset.tpwLogoDigitisation || 'One-off setup'));
    if (line.dataset.tpwDigitisationSource) info.appendChild(make('small', '', line.dataset.tpwDigitisationSource));
    card.appendChild(info);
    card.appendChild(make('strong', '', formatMoney(lineNumber(line, 'tpwLinePrice'), drawer)));
    return card;
  }

  function createCostCell(label, cents, total, drawer) {
    const cell = make('div', 'tpw-drawer-cost' + (total ? ' tpw-drawer-cost--total' : ''));
    cell.appendChild(make('span', '', label));
    cell.appendChild(make('strong', '', formatMoney(cents, drawer)));
    return cell;
  }

  function actionButton(label, className, attribute, value) {
    const button = make('button', 'tpw-drawer-action' + (className ? ' ' + className : ''), label);
    button.type = 'button';
    button.setAttribute(attribute, value || 'true');
    return button;
  }

  function enhanceBundle(drawer, bundleId, lines) {
    const firstLine = lines[0];
    const garments = lines.filter((line) => line.dataset.tpwServiceType === 'garment');
    const logos = lines.filter((line) => line.dataset.tpwServiceType === 'logo');
    const digitisation = lines.filter((line) => line.dataset.tpwServiceType === 'digitisation');
    const quantity = garments.reduce((sum, line) => sum + lineNumber(line, 'tpwQuantity'), 0);
    const itemCost = garments.reduce((sum, line) => sum + lineNumber(line, 'tpwLinePrice'), 0);
    const logoCost = logos.reduce((sum, line) => sum + lineNumber(line, 'tpwLinePrice'), 0);
    const digitisationCost = digitisation.reduce((sum, line) => sum + lineNumber(line, 'tpwLinePrice'), 0);
    const total = itemCost + logoCost + digitisationCost;
    const hasCustomisation = logos.length > 0 || digitisation.length > 0 || garments.some((line) => (
      line.dataset.tpwLogoPosition || line.dataset.tpwLogoCustomisation
    ));
    const bundle = make('section', 'tpw-drawer-bundle');
    const head = make('div', 'tpw-drawer-bundle__head');
    const heading = make('div');
    const summary = make('div', 'tpw-drawer-bundle__summary');
    const costs = make('div', 'tpw-drawer-costs');
    const details = make('div', 'tpw-drawer-bundle__details');
    const actions = make('div', 'tpw-drawer-bundle__actions');
    const detailsId = 'TPWDrawerBundleDetails-' + (++bundleCounter);
    const title = garments.length ? garments[0].dataset.tpwProductTitle : 'Customised garments';
    bundle.dataset.tpwBundle = bundleId;
    heading.appendChild(make('p', 'tpw-drawer-bundle__eyebrow', 'Your customised order'));
    heading.appendChild(make('h2', 'tpw-drawer-bundle__title', title || 'Customised garments'));
    heading.appendChild(make(
      'span', 'tpw-drawer-bundle__count',
      quantity + ' garment' + (quantity === 1 ? '' : 's') + ' across ' + garments.length +
      ' selection' + (garments.length === 1 ? '' : 's')
    ));
    head.appendChild(heading);
    head.appendChild(make('strong', 'tpw-drawer-bundle__price', formatMoney(total, drawer)));
    garments.forEach((line) => summary.appendChild(createProductCard(line, logos, drawer)));
    digitisation.forEach((line) => summary.appendChild(createDigitisationCard(line, drawer)));
    costs.appendChild(createCostCell('Items', itemCost, false, drawer));
    costs.appendChild(createCostCell('Logo customisation', logoCost, false, drawer));
    costs.appendChild(createCostCell('Digitisation', digitisationCost, false, drawer));
    costs.appendChild(createCostCell('Customised total', total, true, drawer));
    details.id = detailsId;
    details.hidden = true;
    firstLine.parentNode.insertBefore(bundle, firstLine);
    lines.forEach((line) => details.appendChild(line));
    const toggle = actionButton('View details', '', 'data-tpw-toggle-bundle', bundleId);
    toggle.setAttribute('aria-controls', detailsId);
    toggle.setAttribute('aria-expanded', 'false');
    actions.appendChild(toggle);
    const editLink = make('a', 'tpw-drawer-action tpw-drawer-action--primary', 'Edit in full basket');
    editLink.href = drawer.dataset.cartUrl || endpoint('cart');
    actions.appendChild(editLink);
    if (hasCustomisation) {
      actions.appendChild(actionButton('Remove customisation', 'tpw-drawer-action--danger', 'data-tpw-remove-customisation', bundleId));
    }
    actions.appendChild(actionButton('Remove customised order', 'tpw-drawer-action--danger', 'data-tpw-remove-bundle', bundleId));
    bundle.appendChild(head);
    bundle.appendChild(summary);
    bundle.appendChild(costs);
    bundle.appendChild(details);
    bundle.appendChild(actions);
  }

  function enhance(drawer) {
    if (!drawer || !drawer.isConnected) return;
    const linesRoot = drawer.querySelector('[data-tpw-drawer-lines]');
    if (!linesRoot || linesRoot.dataset.tpwBundlesReady === 'true') return;
    const allLines = Array.from(linesRoot.querySelectorAll('[data-tpw-drawer-line]'));
    const groups = new Map();
    allLines.forEach((line) => {
      const id = line.dataset.tpwBundleId || '';
      if (!id) return;
      if (!groups.has(id)) groups.set(id, []);
      groups.get(id).push(line);
    });
    let firstBundle = true;
    groups.forEach((lines, bundleId) => {
      if (firstBundle && lines[0]) {
        lines[0].parentNode.insertBefore(make('p', 'tpw-drawer-section-label', 'Your customised selections'), lines[0]);
        firstBundle = false;
      }
      enhanceBundle(drawer, bundleId, lines);
    });
    const firstUngrouped = Array.from(linesRoot.querySelectorAll('[data-tpw-drawer-line]'))
      .find((line) => !line.dataset.tpwBundleId);
    if (groups.size && firstUngrouped) {
      firstUngrouped.parentNode.insertBefore(make('p', 'tpw-drawer-section-label', 'Other basket items'), firstUngrouped);
    }
    linesRoot.dataset.tpwBundlesReady = 'true';
  }

  function scheduleEnhance(drawer) {
    window.requestAnimationFrame(() => enhance(drawer));
  }

  function setBundleBusy(drawer, bundleId, busy) {
    if (busy) busyBundles.add(bundleId);
    else busyBundles.delete(bundleId);
    Array.from(drawer.querySelectorAll('[data-tpw-bundle]')).forEach((bundle) => {
      if (bundle.dataset.tpwBundle === bundleId) {
        bundle.classList.toggle('is-busy', busy);
        bundle.setAttribute('aria-busy', busy ? 'true' : 'false');
      }
    });
  }

  function showError(drawer, error, fallback) {
    const output = drawer.querySelector('#CartDrawer-CartErrors');
    if (output) {
      output.textContent = error && error.message ? error.message : fallback;
      output.setAttribute('tabindex', '-1');
      output.focus();
    }
    console.error(fallback, error);
  }

  async function syncGroupCustomisation(bundleId) {
    if (!bundleId) return;
    const cart = await fetchCart();
    const items = findGroupItems(cart, bundleId);
    const targets = getGroupTargets(items).filter((target) => target.quantity > 0);
    const customisationItems = items.filter(isSeparateCustomisationItem);
    for (let index = 0; index < customisationItems.length; index += 1) {
      const item = customisationItems[index];
      const selectedTargets = targetsForCustomisationItem(item, targets).filter((target) => target.quantity > 0);
      const totalQuantity = selectedTargets.reduce((sum, target) => sum + target.quantity, 0);
      const isDigitisation = itemHandle(item) === 'logo-setup-digitisation';
      const desiredQuantity = isDigitisation ? (totalQuantity > 0 ? 1 : 0) : totalQuantity;
      if (desiredQuantity < 1) {
        await changeLine(item.key, 0);
        continue;
      }
      const properties = cloneProperties(item.properties);
      properties['Applied To'] = formatAppliedTo(selectedTargets);
      if (item.quantity !== desiredQuantity || String(item.properties['Applied To'] || '') !== properties['Applied To']) {
        await changeLine(item.key, desiredQuantity, properties);
      }
    }
  }

  function sectionInnerHTML(html, selector) {
    if (!html) return '';
    const documentNode = new DOMParser().parseFromString(html, 'text/html');
    const element = documentNode.querySelector(selector || '.shopify-section');
    return element ? element.innerHTML : '';
  }

  async function refreshDrawer(drawer, cartState) {
    const ids = ['cart-drawer', drawer.cart_icon, drawer.cart_icon_mobile, 'cart-free-delivery'].filter(Boolean);
    const url = routesRoot() + '?sections=' + encodeURIComponent(ids.join(','));
    const response = await fetch(url, { credentials: 'same-origin', headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('The basket drawer could not be refreshed.');
    const sections = await response.json();
    const currentDrawer = drawer.querySelector('#CartDrawer');
    const incomingDrawer = sectionInnerHTML(sections['cart-drawer'], '#CartDrawer');
    if (!currentDrawer || !incomingDrawer) throw new Error('The refreshed basket drawer was incomplete.');
    currentDrawer.innerHTML = incomingDrawer;
    [drawer.cart_icon, drawer.cart_icon_mobile].filter(Boolean).forEach((id) => {
      const current = document.getElementById(id);
      const incoming = sectionInnerHTML(sections[id], '.shopify-section');
      if (current && incoming) current.innerHTML = incoming;
      if (id === drawer.cart_icon) {
        const footerBubble = document.querySelector('.footer-sticky-nav__item__icon .cart-count-bubble');
        if (footerBubble && incoming) {
          const parsed = new DOMParser().parseFromString(incoming, 'text/html');
          const incomingBubble = parsed.querySelector('.cart-count-bubble');
          footerBubble.innerHTML = incomingBubble ? incomingBubble.innerHTML : '';
        }
      }
    });
    const productPageDelivery = document.querySelector('#product-page-free-delivery');
    const incomingDelivery = sectionInnerHTML(sections['cart-free-delivery'], '.shopify-section');
    if (productPageDelivery && incomingDelivery) productPageDelivery.innerHTML = incomingDelivery;
    const cart = cartState && typeof cartState.item_count !== 'undefined' ? cartState : await fetchCart();
    drawer.classList.toggle('is-empty', cart.item_count < 1);
    scheduleEnhance(drawer);
  }

  async function updateGroupedQuantity(drawer, input) {
    const bundleId = input.dataset.tpwBundleId || '';
    const key = input.dataset.tpwItemKey || '';
    const current = lineNumber(input.closest('[data-tpw-drawer-line]'), 'tpwQuantity');
    const maximum = Math.max(0, intValue(input.max, 100000));
    const minimum = Math.max(1, intValue(input.dataset.moq, 1));
    let quantity = Math.max(0, intValue(input.value, current));
    if (quantity > 0) quantity = Math.max(minimum, Math.min(maximum, quantity));
    if (!bundleId || !key || busyBundles.has(bundleId) || quantity === current) return;
    input.value = quantity;
    setBundleBusy(drawer, bundleId, true);
    try {
      await changeLine(key, quantity);
      await syncGroupCustomisation(bundleId);
      await refreshDrawer(drawer);
      setBundleBusy(drawer, bundleId, false);
    } catch (error) {
      input.value = current;
      setBundleBusy(drawer, bundleId, false);
      showError(drawer, error, 'The quantity could not be updated. Please try again.');
    }
  }

  async function removeGroupedLine(drawer, button) {
    const bundleId = button.dataset.tpwBundleId || '';
    const key = button.dataset.tpwItemKey || '';
    if (!bundleId || !key || busyBundles.has(bundleId)) return;
    setBundleBusy(drawer, bundleId, true);
    try {
      await changeLine(key, 0);
      await syncGroupCustomisation(bundleId);
      await refreshDrawer(drawer);
      setBundleBusy(drawer, bundleId, false);
    } catch (error) {
      setBundleBusy(drawer, bundleId, false);
      showError(drawer, error, 'That item could not be removed. Please try again.');
    }
  }

  function closeConfirmation(confirmed) {
    if (!confirmationDrawer) return false;
    const modal = confirmationDrawer.querySelector('[data-tpw-drawer-confirm]');
    if (modal) modal.hidden = true;
    const resolver = confirmationResolver;
    const previousFocus = confirmationPreviousFocus;
    confirmationResolver = null;
    confirmationPreviousFocus = null;
    confirmationDrawer = null;
    if (previousFocus && previousFocus.isConnected) previousFocus.focus();
    if (resolver) resolver(!!confirmed);
    return true;
  }

  function confirmAction(drawer, options) {
    const modal = drawer.querySelector('[data-tpw-drawer-confirm]');
    const title = modal && modal.querySelector('[data-tpw-drawer-confirm-title]');
    const message = modal && modal.querySelector('[data-tpw-drawer-confirm-message]');
    const accept = modal && modal.querySelector('[data-tpw-drawer-confirm-accept]');
    if (!modal || !title || !message || !accept) return Promise.resolve(false);
    if (confirmationResolver) closeConfirmation(false);
    confirmationDrawer = drawer;
    confirmationPreviousFocus = document.activeElement;
    title.textContent = options.title || 'Confirm removal';
    message.textContent = options.message || 'Are you sure you want to remove this?';
    accept.textContent = options.confirmLabel || 'Remove';
    modal.hidden = false;
    window.setTimeout(() => accept.focus(), 0);
    return new Promise((resolve) => { confirmationResolver = resolve; });
  }

  async function removeWholeBundle(drawer, button) {
    const bundleId = button.dataset.tpwRemoveBundle || '';
    if (!bundleId || busyBundles.has(bundleId)) return;
    const confirmed = await confirmAction(drawer, {
      title: 'Remove customised order',
      message: 'Remove this customised order and all linked logo charges?',
      confirmLabel: 'Remove order',
    });
    if (!confirmed) return;
    setBundleBusy(drawer, bundleId, true);
    try {
      const cart = await fetchCart();
      const updates = {};
      findGroupItems(cart, bundleId).forEach((item) => { updates[item.key] = 0; });
      const updatedCart = Object.keys(updates).length ? await updateCartQuantities(updates) : cart;
      await refreshDrawer(drawer, updatedCart);
      setBundleBusy(drawer, bundleId, false);
    } catch (error) {
      setBundleBusy(drawer, bundleId, false);
      showError(drawer, error, 'The customised order could not be removed.');
    }
  }

  function stripCustomisationProperties(properties, removeBundleId) {
    const cleaned = cloneProperties(properties);
    LOGO_PROPERTY_NAMES.forEach((name) => { delete cleaned[name]; });
    if (removeBundleId) delete cleaned['Custom Bundle ID'];
    return cleaned;
  }

  async function removeGroupCustomisation(drawer, button) {
    const bundleId = button.dataset.tpwRemoveCustomisation || '';
    if (!bundleId || busyBundles.has(bundleId)) return;
    const confirmed = await confirmAction(drawer, {
      title: 'Remove customisation',
      message: 'Remove all logo customisation from these garments? The garments will remain in your basket.',
      confirmLabel: 'Remove customisation',
    });
    if (!confirmed) return;
    setBundleBusy(drawer, bundleId, true);
    try {
      let cart = await fetchCart();
      let groupItems = findGroupItems(cart, bundleId);
      const keepBundleId = groupItems.some((item) => item.properties && item.properties['Bundle Contents']);
      const updates = {};
      groupItems.forEach((item) => {
        if (isSeparateCustomisationItem(item)) updates[item.key] = 0;
      });
      if (Object.keys(updates).length) await updateCartQuantities(updates);
      cart = await fetchCart();
      groupItems = findGroupItems(cart, bundleId).filter((item) => !isSeparateCustomisationItem(item));
      for (let index = 0; index < groupItems.length; index += 1) {
        const item = groupItems[index];
        await changeLine(item.key, item.quantity, stripCustomisationProperties(item.properties, !keepBundleId));
      }
      await refreshDrawer(drawer);
      setBundleBusy(drawer, bundleId, false);
    } catch (error) {
      setBundleBusy(drawer, bundleId, false);
      showError(drawer, error, 'The logo customisation could not be removed.');
    }
  }

  function handleClick(drawer, event) {
    const cancel = event.target.closest('[data-tpw-drawer-confirm-cancel]');
    if (cancel && drawer.contains(cancel)) {
      event.preventDefault();
      event.stopImmediatePropagation();
      closeConfirmation(false);
      return;
    }
    const accept = event.target.closest('[data-tpw-drawer-confirm-accept]');
    if (accept && drawer.contains(accept)) {
      event.preventDefault();
      event.stopImmediatePropagation();
      closeConfirmation(true);
      return;
    }
    if (confirmationDrawer === drawer) return;
    const toggle = event.target.closest('[data-tpw-toggle-bundle]');
    if (toggle && drawer.contains(toggle)) {
      event.preventDefault();
      const controls = toggle.getAttribute('aria-controls');
      const details = controls ? document.getElementById(controls) : null;
      if (!details) return;
      const opening = details.hidden;
      details.hidden = !opening;
      toggle.setAttribute('aria-expanded', opening ? 'true' : 'false');
      toggle.textContent = opening ? 'Hide details' : 'View details';
      return;
    }
    const removeLine = event.target.closest('[data-tpw-drawer-remove-line]');
    if (removeLine && removeLine.dataset.tpwBundleId) {
      event.preventDefault();
      event.stopImmediatePropagation();
      removeGroupedLine(drawer, removeLine);
      return;
    }
    const removeCustomisation = event.target.closest('[data-tpw-remove-customisation]');
    if (removeCustomisation && drawer.contains(removeCustomisation)) {
      event.preventDefault();
      event.stopImmediatePropagation();
      removeGroupCustomisation(drawer, removeCustomisation);
      return;
    }
    const removeBundle = event.target.closest('[data-tpw-remove-bundle]');
    if (removeBundle && drawer.contains(removeBundle)) {
      event.preventDefault();
      event.stopImmediatePropagation();
      removeWholeBundle(drawer, removeBundle);
    }
  }

  function handleChange(drawer, event) {
    const input = event.target.closest('[data-tpw-drawer-bundle-quantity]');
    if (!input || !input.dataset.tpwBundleId || !drawer.contains(input)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    updateGroupedQuantity(drawer, input);
  }

  function mount(drawer) {
    if (!drawer || drawer.dataset.tpwDrawerMounted === 'true') return;
    drawer.dataset.tpwDrawerMounted = 'true';
    drawer.addEventListener('click', (event) => handleClick(drawer, event), true);
    drawer.addEventListener('change', (event) => handleChange(drawer, event), true);
    const observer = new MutationObserver(() => scheduleEnhance(drawer));
    observer.observe(drawer, { childList: true, subtree: true });
    drawer.tpwBundleObserver = observer;
    scheduleEnhance(drawer);
  }

  window.TPWCartDrawer = {
    mount,
    enhance,
    scheduleEnhance,
    closeConfirmation,
    hasOpenConfirmation(drawer) { return confirmationDrawer === drawer; },
  };

  class CartDrawer extends HTMLElement {
    constructor() {
      super();
      this.cart_icon = document.querySelector('#cart-icon-bubble-short') ? 'cart-icon-bubble-short' : 'cart-icon-bubble';
      this.cart_icon_mobile = document.querySelector('#cart-icon-bubble--mobile-short') ? 'cart-icon-bubble--mobile-short' : 'cart-icon-bubble--mobile';
      this.addEventListener('keyup', (event) => {
        if (event.code !== 'Escape') return;
        if (window.TPWCartDrawer.hasOpenConfirmation(this)) {
          event.preventDefault();
          event.stopImmediatePropagation();
          window.TPWCartDrawer.closeConfirmation(false);
          return;
        }
        this.close();
      });
      this.setHeaderCartIconAccessibility();
    }

    connectedCallback() {
      window.TPWCartDrawer.mount(this);
    }

    setHeaderCartIconAccessibility() {
      document.querySelectorAll('[id*="cart-icon-bubble"]').forEach((cartLink) => {
        cartLink.setAttribute('role', 'button');
        cartLink.setAttribute('aria-haspopup', 'dialog');
        cartLink.addEventListener('click', (event) => {
          event.preventDefault();
          this.open(cartLink);
        });
        cartLink.addEventListener('keydown', (event) => {
          if (event.code.toUpperCase() === 'SPACE') {
            event.preventDefault();
            this.open(cartLink);
          }
        });
      });
    }

    open(triggeredBy) {
      if (triggeredBy) this.setActiveElement(triggeredBy);
      const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
      if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
      window.TPWCartDrawer.scheduleEnhance(this);
      setTimeout(() => {
        this.classList.add('active');
        this.classList.add('animate');
      });
      this.addEventListener('transitionend', () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        if (containerToTrapFocusOn && focusElement) trapFocus(containerToTrapFocusOn, focusElement);
      }, { once: true });
      if (document.querySelectorAll('.popup-modal.active').length === 0) {
        document.body.style.paddingRight = this.getScrollbarWidth() + 'px';
        document.body.classList.add('overflow-hidden');
      }
    }

    close() {
      if (window.TPWCartDrawer.hasOpenConfirmation(this)) {
        window.TPWCartDrawer.closeConfirmation(false);
        return;
      }
      this.classList.remove('animate');
      setTimeout(() => {
        this.classList.remove('active');
        if (document.querySelectorAll('.popup-modal.active').length === 0) {
          document.body.classList.remove('overflow-hidden');
          document.body.style.paddingRight = '';
        }
      }, 500);
      removeTrapFocus(this.activeElement);
    }

    setSummaryAccessibility(cartDrawerNote) {
      cartDrawerNote.setAttribute('role', 'button');
      cartDrawerNote.setAttribute('aria-expanded', 'false');
      if (cartDrawerNote.nextElementSibling && cartDrawerNote.nextElementSibling.getAttribute('id')) {
        cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
      }
      cartDrawerNote.addEventListener('click', (event) => {
        event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
      });
      cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
    }

    renderContents(parsedState) {
      this.classList.remove('is-empty');
      const drawerInner = this.querySelector('.drawer__inner');
      if (drawerInner && drawerInner.classList.contains('is-empty')) drawerInner.classList.remove('is-empty');
      this.productId = parsedState.id;
      this.getSectionsToRender().forEach((section) => {
        const sectionElement = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
        if (sectionElement && parsedState.sections[section.id]) {
          sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
        }
        if (section.id === 'cart-free-delivery' && document.querySelector('#product-page-free-delivery')) {
          document.querySelector('#product-page-free-delivery').innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
        }
        if (section.id === this.cart_icon && document.querySelector('.footer-sticky-nav__item__icon .cart-count-bubble')) {
          const parser = new DOMParser();
          const bubble = parser.parseFromString(this.getSectionInnerHTML(parsedState.sections[section.id], section.selector), 'text/html');
          const countBubble = bubble.querySelector('.cart-count-bubble');
          document.querySelector('.footer-sticky-nav__item__icon .cart-count-bubble').innerHTML = countBubble ? countBubble.innerHTML : '';
        }
      });
      window.TPWCartDrawer.scheduleEnhance(this);
      setTimeout(() => this.open());
    }

    getSectionInnerHTML(html, selector = '.shopify-section') {
      return sectionInnerHTML(html, selector);
    }

    getSectionsToRender() {
      return [
        { id: 'cart-drawer', selector: '#CartDrawer' },
        { id: this.cart_icon },
        { id: this.cart_icon_mobile },
        { id: 'cart-free-delivery' },
      ];
    }

    getSectionDOM(html, selector = '.shopify-section') {
      return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    }

    setActiveElement(element) {
      this.activeElement = element;
    }

    getScrollbarWidth() {
      return window.innerWidth - document.documentElement.clientWidth;
    }
  }

  if (!customElements.get('cart-drawer')) customElements.define('cart-drawer', CartDrawer);

  class CartDrawerItems extends CartItems {
    getSectionsToRender() {
      this.cart_icon = document.querySelector('#cart-icon-bubble-short') ? 'cart-icon-bubble-short' : 'cart-icon-bubble';
      this.cart_icon_mobile = document.querySelector('#cart-icon-bubble--mobile-short') ? 'cart-icon-bubble--mobile-short' : 'cart-icon-bubble--mobile';
      return [
        { id: 'CartDrawer', section: 'cart-drawer', selector: '.drawer__inner' },
        { id: this.cart_icon, section: this.cart_icon, selector: '.shopify-section' },
        { id: this.cart_icon_mobile, section: this.cart_icon_mobile, selector: '.shopify-section' },
      ];
    }
  }

  if (!customElements.get('cart-drawer-items')) customElements.define('cart-drawer-items', CartDrawerItems);
})();